import React, { useEffect, useRef } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { IntlProvider } from 'react-intl';
import { setLoadedMessages, setMessages } from '@actions/common/actionCreators';
import { toHtmlLang } from '@utils/miscellaneous/languageCodes';
import Spinner from '../components/common/spinner/Spinner';

/**
 *
 * @param {Object} props
 * @returns {React.Component}
 *
 * Here we inject in pre-built component <IntlProvider/> messages
 * (they go from Redux Store on successful download from server)
 * and make <IntlProvider/> to update every time when we switch languages
 */
const ConnectedIntlProvider = props => {
    const {
        actions: { setLoadedMessages, setMessages },
        children,
        environment: { isMNFullStack, profile, stateCode, language },
        messages,
        loadedLanguages,
    } = props;

    const contentProfileKey = profile ?? stateCode;

    useEffect(() => {
        if (contentProfileKey) {
            const messagesLocale = contentProfileKey.toLowerCase();

            // Load both base and profile-specific messages dynamically
            Promise.all([
                import(`../i18n/base/messages_${language}.json`),
                import(`../i18n/${messagesLocale}/messages_${language}${isMNFullStack ? '_fs' : ''}.json`),
            ])
                .then(([baseMessages, stateMessages]) => {
                    // Merge base messages with profile-specific overrides
                    const mergedMessages = { default: { ...baseMessages.default, ...stateMessages.default } };
                    setMessages(mergedMessages, language);

                    if (loadedLanguages && !loadedLanguages.includes(language)) {
                        loadedLanguages.push(language);
                        setLoadedMessages(loadedLanguages);
                    }
                })
                .catch(() => {
                    if (language === 'debug') {
                        setMessages({}, language);
                    }
                });
        }
    }, [language, contentProfileKey, isMNFullStack]);

    // Keep rendering with previously loaded messages while new language is loading
    const lastReadyLangRef = useRef(null);
    const lastReadyMessagesRef = useRef(null);

    const hasCurrentLangMessages = language === 'debug' ? 'true' : Boolean(messages);
    if (hasCurrentLangMessages) {
        lastReadyLangRef.current = language;
        lastReadyMessagesRef.current = messages;
    }

    const effectiveLanguage = hasCurrentLangMessages ? language : lastReadyLangRef.current || language;
    const effectiveMessages = hasCurrentLangMessages ? messages : lastReadyMessagesRef.current || messages || {};
    const isSwitchingLanguage = !hasCurrentLangMessages && Boolean(lastReadyMessagesRef.current);

    // Keyed on the rendered locale rather than on `language` so the attribute flips
    // at the same moment the content does, not while the switch is still loading.
    useEffect(() => {
        document.documentElement.lang = toHtmlLang(effectiveLanguage);
    }, [effectiveLanguage]);

    return (
        <IntlProvider locale={effectiveLanguage} messages={effectiveMessages}>
            {children}
            {isSwitchingLanguage && <Spinner message="common.loading" />}
        </IntlProvider>
    );
};

const mapStateToProps = state => ({
    environment: state.common.environment,
    messages: state.common.messages && state.common.messages[state.common.environment.language],
    loadedLanguages: state.common.messages && state.common.messages.loadedLanguages,
});

const mapDispatchToProps = dispatch => ({
    actions: bindActionCreators(
        {
            setLoadedMessages,
            setMessages,
        },
        dispatch,
    ),
});

export { ConnectedIntlProvider };

export default connect(mapStateToProps, mapDispatchToProps)(ConnectedIntlProvider);
